<template>
    <hox-modal class="importExportModal" @close="close">
        <template #header>Import/Export</template>

        <div>
            <hox-alert type="info" class="importExportModal__howto">
                <template #title>To upload content</template>
                <template #content>
                    <ol class="importExportModal__howto-list">
                        <li>
                            <span
                                class="link-like"
                                data-testid="import-export-modal__download-template-link"
                                @click="downloadTemplate"
                            >
                                Download template
                            </span>
                        </li>
                        <li>Fill in the content in the the file</li>
                        <li>Upload the content XSLX file</li>
                    </ol>
                </template>
            </hox-alert>

            <div class="importExportModal__upload-controls">
                <xls-upload-button
                    @click.stop="valuesUploadCompleted = false"
                    @load="onXlsValuesLoad"
                    @error="onValuesImportError"
                >
                    <Button type="primary" class="">Upload Selection</Button>
                </xls-upload-button>
            </div>

            <div class="importExportModal__upload-results">
                <div
                    v-if="fileName"
                    class="importExportModal__upload-result__file"
                    data-testid="import-export-modal__uploaded-file-name"
                >
                    {{ fileName }} ({{ validRows.length }} rows)
                </div>

                <hox-alert
                    v-if="hasValuesError"
                    type="danger"
                    class="importExportModal__upload-result"
                    data-testid="import-export-modal__upload-error"
                >
                    <template #title>There were problems reading this sheet</template>

                    <template #content>
                        <ul v-if="valuesErrorDetails" class="importExportModal__upload-results-error-list">
                            <li v-for="detail in valuesErrorDetails" :key="detail">{{ detail }}</li>
                        </ul>
                    </template>
                </hox-alert>
            </div>
        </div>

        <template #footer>
            <div class="share-preview-modal__footer-button-wrapper">
                <Button data-testid="import-export-modal__close-button" @click="close">Close</Button>
                <Button
                    v-if="valuesUploadCompleted && !hasValuesError"
                    class="importExportModal__button"
                    type="primary"
                    data-testid="import-export-modal__apply-selection-button"
                    @click="applySelection"
                >
                    Apply selection
                </Button>
                <Button
                    v-if="valuesUploadCompleted && !hasValuesError"
                    class="importExportModal__button"
                    data-testid="import-export-modal__download-button"
                    type="primary"
                    @click="download"
                >
                    Download
                </Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import { CampaignGetters } from "@/store/modules/campaign";
import ImportExport from "@/services/ImportExport";
import XlsUploadButton from "@/components/Campaign/XlsUploadButton.vue";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    name: "ImportExportLibraryModal",
    components: {
        XlsUploadButton,
        HoxModal
    },

    emits: ["close", "applySelection", "exportTemplate", "download"],

    setup() {
        const ga4 = useGA4Tracking();
        return {
            ga4
        };
    },

    data() {
        return {
            hasValuesError: false,
            validRows: [],
            valuesErrorDetails: [],
            valuesUploadCompleted: false,
            fileName: "",
            reportingLabels: [],
            importExportTracked: false
        };
    },

    computed: {
        campaignLanguages() {
            return this.$store.state.campaign.languages;
        },

        masterTemplateNameMap() {
            return this.$store.getters[CampaignGetters.masterTemplatesBySize];
        },

        validGroupValuesNamesByGroupValue() {
            return this.$store.getters[CampaignGetters.groupValueNamesByGroup];
        },

        validLanguages() {
            return this.campaignLanguages;
        },

        validMasterTemplateLabel() {
            return Object.keys(this.masterTemplateNameMap);
        }
    },

    created() {
        this.importExportService = new ImportExport(this.$apollo, this.$store);
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
            featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT
        });
    },

    methods: {
        async onXlsValuesLoad(importedData) {
            this.valuesUploadCompleted = false;
            this.hasValuesError = false;
            this.valuesErrorDetails = [];

            if (!importedData.sheets.length) {
                this.valuesErrorDetails = [
                    "The file is either empty or is incorrectly formatted. Please check your data and try again."
                ];
                this.hasValuesError = true;
                return;
            }

            await this.$nextTick();

            const processingErrors = [];
            const inputSheet = importedData.Input;
            await this.processInputSelect(inputSheet.sheetData);

            if (processingErrors.length) {
                this.hasValuesError = true;
                this.valuesErrorDetails = processingErrors.flatMap(({ details }) => details);
                this.ga4.trackError({
                    errorMessage: this.valuesErrorDetails.join("; "),
                    errorType: GA4.ERROR_TYPES.SYSTEM_ERROR,
                    origin: GA4.ERROR_ORIGINS.FRONTEND,
                    featureName: GA4.FEATURE_CATEGORIES.PREFLIGHT
                });
            }

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
                featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT
            });
            this.importExportTracked = true;

            this.valuesUploadCompleted = true;
            this.fileName = importedData.fileName;
        },

        async processInputSelect(dataArray) {
            this.validRows = [];
            this.hasValuesError = false;
            this.valuesErrorDetails = [];
            // empty value represents templateName header
            const [, , ...groupValueNames] = dataArray.shift();
            groupValueNames.pop();

            const { validRows, sameValueIndexes, reportingLabels } = dataArray.reduce(
                (acc, row, i) => {
                    const rowIndex = i + 3;
                    const validationError = this.getValidationError(row, groupValueNames, rowIndex);
                    const valueString = JSON.stringify(row);
                    if (acc.sameValueIndexes[valueString]) {
                        acc.sameValueIndexes[valueString].push(rowIndex);
                    } else {
                        acc.sameValueIndexes[valueString] = [rowIndex];
                    }

                    const reportingLabel = row[row.length - 1];
                    if (reportingLabel) {
                        const labels = reportingLabel
                            .split(",")
                            .map(label => label.trim())
                            .filter(label => label);

                        for (const label of labels) {
                            acc.reportingLabels.add(label);
                        }
                    }

                    if (validationError.length) {
                        this.hasValuesError = true;
                        this.valuesErrorDetails.push(...validationError);
                        return acc;
                    }

                    acc.validRows.push(this.processRow(row, groupValueNames));
                    return acc;
                },
                { validRows: [], sameValueIndexes: {}, reportingLabels: new Set() }
            );

            Object.entries(sameValueIndexes).forEach(([values, indexes]) => {
                if (indexes.length > 1) {
                    this.hasValuesError = true;
                    this.valuesErrorDetails.push(
                        `Rows ${indexes.join(", ")} have duplicated values (${JSON.parse(values).join(", ")})`
                    );
                }
            });

            this.validRows = validRows;
            this.reportingLabels = [...reportingLabels.values()];
        },

        close() {
            if (!this.importExportTracked) {
                this.importExportTracked = true;
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
                    featureContext: GA4.FEATURE_CONTEXTS.CLOSE_IMPORT_EXPORT
                });
            }
            this.$emit("close");
        },

        download() {
            this.$emit("download", this.validRows);
        },

        applySelection() {
            this.$emit("applySelection", this.validRows, this.reportingLabels);
        },

        downloadTemplate() {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.DOWNLOAD_TEMPLATE
            });
            this.$emit("exportTemplate");
        },

        getValidationError(row, groupNames, rowNumber) {
            const [size, language, ...values] = row;
            const errors = [];

            if (!this.validMasterTemplateLabel.includes(size)) {
                errors.push(`Invalid size at row ${rowNumber}`);
            }

            if (!this.validLanguages.includes(language)) {
                errors.push(`Invalid language at row ${rowNumber}`);
            }

            values.forEach((value, i) => {
                if (
                    groupNames[i] &&
                    !this.validGroupValuesNamesByGroupValue[groupNames[i]].editableGroupValuesNames.includes(value)
                ) {
                    errors.push(`Invalid value "${value}" for group "${groupNames[i]}" at row ${rowNumber}`);
                }
            });

            return errors;
        },

        onValuesImportError(e) {
            this.hasValuesError = true;
            this.valuesErrorDetails = [e.message];
        },

        processRow(row, groupNames) {
            const [size, language, ...values] = row;

            const editableGroupValueIds = values
                .map(
                    (value, i) =>
                        this.validGroupValuesNamesByGroupValue[groupNames[i]]?.editableGroupValuesNamesMap?.[value]
                )
                .filter(Boolean);
            // todo: what to do with duplicated labels?
            const masterTemplateId = this.masterTemplateNameMap[size][0];

            return {
                language,
                masterTemplateId,
                editableGroupValueIds
            };
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.importExportModal {
    &__navigation {
        margin-bottom: $spacing;
    }

    &__howto {
        &-list {
            padding-left: $spacing;
        }
    }

    &__files {
        padding-left: $spacing;
        margin-bottom: $spacing;
    }

    &__upload-result {
        margin-top: $spacing;
    }

    &__upload-results-error-list {
        padding-left: $spacing;
        word-break: break-word;
    }

    &__upload-controls {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;

        .labeled-switch {
            flex: 0;

            &__label {
                white-space: nowrap;
            }
        }
    }
}

.importExportModal__button:not(:first-child) {
    margin-left: $spacing-small;
}

.importExportModal__upload-result__file {
    margin-top: $spacing-small;
}
</style>
